import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { supabase, invokeFunction } from '@/integrations/supabase/client';
import { setDeviceMfaCookie } from '@/utils/deviceMfaCookie';
import { handleTwilioError } from '@/utils/twilioErrorHandler';
import { updateOrganizationPhoneVerification } from '@/utils/organization/updatePhoneVerification';
import { validatePhoneNumber, formatPhoneNumber, getPhoneNumberExample } from '@/utils/phoneValidation';

interface MfaVerificationProps {
  onComplete: () => void;
  onCancel?: () => void;
  userEmail?: string;
  phoneNumber?: string;
  codeSent?: boolean;
  allowChangePhone?: boolean;
  skipOrgPhoneUpdate?: boolean;
  onPhoneNumberChange?: (phone: string) => void;
}

const MfaVerification: React.FC<MfaVerificationProps> = ({
  onComplete,
  onCancel,
  userEmail,
  phoneNumber: phoneNumberProp,
  codeSent = false,
  allowChangePhone = false,
  skipOrgPhoneUpdate = false,
  onPhoneNumberChange,
}) => {
  const [viewMode, setViewMode] = useState<'verify' | 'changePhone'>('verify');
  const [activePhoneNumber, setActivePhoneNumber] = useState(phoneNumberProp || '');
  const [changePhoneInput, setChangePhoneInput] = useState('');
  const [changePhoneError, setChangePhoneError] = useState('');
  const [phoneChanged, setPhoneChanged] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [sendCodeLoading, setSendCodeLoading] = useState(false);
  const [localCodeSent, setLocalCodeSent] = useState(codeSent);
  const [errorState, setErrorState] = useState<{
    message: string;
    type: 'validation' | 'service' | 'rate_limit' | 'authentication' | 'generic';
    canRetry: boolean;
    suggestAlternative: boolean;
  } | null>(null);
  const { verifyMfa, enrollMfa } = useAuth();

  const resendingRef = useRef(false);
  const lastResendTime = useRef(0);

  useEffect(() => {
    if (phoneNumberProp) {
      setActivePhoneNumber(phoneNumberProp);
    }
  }, [phoneNumberProp]);

  useEffect(() => {
    setLocalCodeSent(codeSent);
  }, [codeSent]);

  const handleEnrollMfaError = (enrollError: {
    errorType?: string;
    twilioError?: Parameters<typeof handleTwilioError>[0];
    error?: string;
  }) => {
    if (enrollError.errorType === 'twilio_api' && enrollError.twilioError) {
      const twilioErrorResponse = handleTwilioError(enrollError.twilioError);
      setErrorState({
        message: twilioErrorResponse.userMessage,
        type: twilioErrorResponse.errorType,
        canRetry: twilioErrorResponse.canRetry,
        suggestAlternative: false,
      });
    } else if (enrollError.errorType === 'rate_limit') {
      setErrorState({
        message: 'Too many code requests. Please wait before requesting another code.',
        type: 'rate_limit',
        canRetry: false,
        suggestAlternative: false,
      });
    } else {
      setError(enrollError.error || 'Failed to send code. Please try again.');
    }
  };

  const sendCodeToPhone = async (phone: string): Promise<boolean> => {
    if (!userEmail) return false;

    const requestBody = {
      email: userEmail,
      factorName: 'SMS Verification',
      factorType: 'sms',
      phoneNumber: phone,
    };

    const response = await invokeFunction(supabase, 'enroll-mfa', {
      body: requestBody,
    });

    const { error: enrollError } = response || {};

    if (enrollError) {
      console.error('[MfaVerification] Error sending code:', enrollError);
      handleEnrollMfaError(enrollError);
      return false;
    }

    return true;
  };

  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    if (!userEmail) {
      setError('Email is required for MFA setup');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setErrorState(null);

      const actualResult = phoneChanged
        ? await enrollMfa(verificationCode, 'sms', userEmail, activePhoneNumber)
        : await verifyMfa(verificationCode, userEmail, activePhoneNumber, 'sms');

      if (actualResult.success) {
        sessionStorage.setItem('figabl_mfa_completed', 'true');
        await setDeviceMfaCookie(userEmail);

        if (activePhoneNumber && userEmail) {
          sessionStorage.setItem(`figabl_mfa_phone_${userEmail}`, activePhoneNumber);
        }

        if (!skipOrgPhoneUpdate && activePhoneNumber) {
          try {
            await updateOrganizationPhoneVerification(userEmail, activePhoneNumber);
          } catch (updateError) {
            console.error('[MfaVerification] Failed to update organization phone verification:', updateError);
          }
        }

        onComplete();
      } else {
        if (actualResult.error?.toLowerCase().includes('expired')) {
          setErrorState({
            message: 'Verification code has expired. Please request a new code.',
            type: 'service',
            canRetry: true,
            suggestAlternative: false,
          });
        } else if (actualResult.error?.includes('Invalid verification code')) {
          setErrorState({
            message: 'Invalid verification code. Please check the code and try again.',
            type: 'validation',
            canRetry: true,
            suggestAlternative: false,
          });
        } else {
          setError(actualResult.error || 'Invalid verification code');
        }
      }
    } catch (verifyError: unknown) {
      console.error('[MfaVerification] Error verifying MFA:', verifyError);
      setError('Failed to verify code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!userEmail) return;

    const now = Date.now();
    const timeSinceLastResend = now - lastResendTime.current;
    const minimumInterval = 5000;

    if (resendingRef.current) {
      setErrorState({
        message: 'Please wait a few seconds before requesting another code.',
        type: 'rate_limit',
        canRetry: false,
        suggestAlternative: false,
      });
      return;
    }

    if (timeSinceLastResend < minimumInterval && lastResendTime.current > 0) {
      setErrorState({
        message: 'Please wait a few seconds before requesting another code.',
        type: 'rate_limit',
        canRetry: false,
        suggestAlternative: false,
      });
      return;
    }

    resendingRef.current = true;
    lastResendTime.current = now;

    try {
      setResendLoading(true);
      setError('');
      setErrorState(null);

      const sent = await sendCodeToPhone(activePhoneNumber);
      if (sent) {
        setLocalCodeSent(true);
        setError('');
      }
    } catch (resendError: unknown) {
      console.error('[MfaVerification] Error resending code:', resendError);
      setError('Failed to resend code. Please try again.');
    } finally {
      setResendLoading(false);
      setTimeout(() => {
        resendingRef.current = false;
      }, 2000);
    }
  };

  const validateAndFormatChangePhone = (value: string) => {
    const formatted = formatPhoneNumber(value);
    const validation = validatePhoneNumber(formatted);

    if (!validation.isValid && value.length > 0) {
      setChangePhoneError(validation.error || '');
    } else {
      setChangePhoneError('');
    }

    setChangePhoneInput(formatted);
  };

  const handleSendCodeToNewPhone = async () => {
    const validation = validatePhoneNumber(changePhoneInput);
    if (!validation.isValid) {
      setChangePhoneError(validation.error || 'Invalid phone number');
      return;
    }

    if (!userEmail) return;

    try {
      setSendCodeLoading(true);
      setError('');
      setErrorState(null);
      setChangePhoneError('');

      const sent = await sendCodeToPhone(changePhoneInput);
      if (!sent) return;

      setActivePhoneNumber(changePhoneInput);
      setPhoneChanged(true);
      onPhoneNumberChange?.(changePhoneInput);
      setVerificationCode('');
      setLocalCodeSent(true);
      setViewMode('verify');
    } catch (sendError: unknown) {
      console.error('[MfaVerification] Error sending code to new phone:', sendError);
      setError('Failed to send code. Please try again.');
    } finally {
      setSendCodeLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && verificationCode.length === 6) {
      handleVerify();
    }
  };

  if (viewMode === 'changePhone') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Shield className="w-12 h-12 mx-auto mb-4 text-blue-600" />
            <CardTitle>Use a Different Number</CardTitle>
            <CardDescription>
              Not your phone? Enter the number where you want to receive the code.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive" role="alert">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {errorState && (
              <Alert variant={errorState.type === 'validation' ? 'destructive' : 'warning'} role="alert">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{errorState.message}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="change-phone-number">Phone Number</Label>
              <Input
                id="change-phone-number"
                placeholder={getPhoneNumberExample()}
                value={changePhoneInput}
                onChange={(e) => validateAndFormatChangePhone(e.target.value)}
                type="tel"
                className={changePhoneError ? 'border-red-500' : ''}
                autoFocus
              />
              {changePhoneError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{changePhoneError}</AlertDescription>
                </Alert>
              )}
              <p className="text-xs text-gray-500">
                Include country code (e.g., {getPhoneNumberExample()})
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleSendCodeToNewPhone}
                disabled={sendCodeLoading || !!changePhoneError || !changePhoneInput}
                className="flex-1"
              >
                {sendCodeLoading ? 'Sending...' : 'Send Code'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setViewMode('verify');
                  setChangePhoneInput('');
                  setChangePhoneError('');
                  setError('');
                  setErrorState(null);
                }}
                disabled={sendCodeLoading}
              >
                Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <Shield className="w-12 h-12 mx-auto mb-4 text-blue-600" />
          <CardTitle>Two-Factor Authentication</CardTitle>
          <CardDescription>
            Enter the 6-digit code from your SMS to continue.
          </CardDescription>
          {activePhoneNumber && (
            <p className="text-sm text-gray-600 mt-2">
              Code sent to: <strong>{activePhoneNumber}</strong>
            </p>
          )}
          {localCodeSent && (
            <div className="flex items-center justify-center gap-2 mt-2 text-sm text-green-600">
              <CheckCircle className="h-4 w-4" />
              <span>Verification code sent!</span>
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive" role="alert">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {errorState && (
            <Alert variant={errorState.type === 'validation' ? 'destructive' : 'warning'} role="alert">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {errorState.message}
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="verification-code">Verification Code</Label>
            <Input
              id="verification-code"
              data-testid="mfa-code"
              placeholder="123456"
              value={verificationCode}
              onChange={(e) => {
                setError('');
                setErrorState(null);
                setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6));
              }}
              onBlur={() => {
                if (verificationCode.length > 0 && verificationCode.length !== 6) {
                  setError('Please enter a valid 6-digit code');
                }
              }}
              onKeyPress={handleKeyPress}
              className="text-center text-lg tracking-widest"
              autoComplete="one-time-code"
              autoFocus
            />
          </div>

          <div className="flex gap-2">
            <Button
              data-testid="verify-mfa"
              onClick={handleVerify}
              disabled={loading || verificationCode.length !== 6}
              className="flex-1"
            >
              {loading ? 'Verifying...' : 'Verify'}
            </Button>
            {onCancel && (
              <Button variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            )}
          </div>

          <div className="text-center space-y-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResendCode}
              disabled={resendLoading || !userEmail || errorState?.type === 'rate_limit'}
            >
              {resendLoading ? 'Sending...' : 'Resend Code'}
            </Button>
            {allowChangePhone && (
              <div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setChangePhoneInput('');
                    setChangePhoneError('');
                    setViewMode('changePhone');
                    setError('');
                    setErrorState(null);
                  }}
                  disabled={resendLoading || loading}
                >
                  Use a different number
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MfaVerification;
